import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import Masonry from 'react-masonry-css';
import PhotoTile from './PhotoTile';

interface Photo {
  photoId: string;
  imageUrl: string;
  thumbnailUrls?: {
    small: string;
    medium: string;
    large: string;
  };
  ownerId: string;
  ownerName: string;
  likeCount: number;
  userHasLiked?: boolean;
  aspectRatio?: number;
  createdAt: string;
}

interface PhotoGridProps {
  photos: Photo[];
  onLoadMore: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onPhotoDeleted: () => void;
  selectedPhotoId?: string;
}

export default function PhotoGrid({
  photos,
  onLoadMore,
  hasMore,
  isLoadingMore,
  onPhotoDeleted,
  selectedPhotoId,
}: PhotoGridProps) {
  const { ref, inView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  }, [inView, hasMore, isLoadingMore, onLoadMore]);

  useEffect(() => {
    if (selectedPhotoId) {
    }
  }, [selectedPhotoId, photos]);

  const breakpointColumns = {
    default: 12,   // 2560px+: 12 columns for ultra-ultra-wide screens
    2400: 11,      // 2400px: 11 columns
    2200: 10,      // 2200px: 10 columns
    2048: 9,       // 2048px: 9 columns
    1920: 8,       // 1920px: 8 columns for very large screens
    1728: 7,       // 1728px: 7 columns
    1536: 6,       // 1536px (2xl): 6 columns
    1280: 5,       // 1280px (xl): 5 columns
    1024: 3,       // 1024px (lg): 3 columns
    768: 2,        // 768px (md): 2 columns
    640: 2,        // 640px (sm): 2 columns
    480: 1,        // 480px: 1 column (mobile)
  };

  return (
    <div>
      <Masonry
        breakpointCols={breakpointColumns}
        className="flex -ml-1 w-auto"
        columnClassName="pl-1 bg-clip-padding"
      >
        {photos.map((photo) => (
          <PhotoTile
            key={photo.photoId}
            photo={photo}
            onDeleted={onPhotoDeleted}
            initiallyOpen={photo.photoId === selectedPhotoId}
          />
        ))}
      </Masonry>

      {/* Sentinel for infinite scroll */}
      {hasMore && (
        <div ref={ref} className="flex justify-center py-8">
          {isLoadingMore && (
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-primary-500"></div>
          )}
        </div>
      )}
    </div>
  );
}

